import * as THREE from 'three';
import { Loop } from './core/Loop';
import { GameClock } from './core/Time';
import { Settings } from './core/Settings';
import { EventBus } from './core/Events';
import { hashString } from './core/Rng';
import { RenderSystem } from './render/Renderer';
import { InputManager } from './input/InputManager';
import { TouchControls } from './input/TouchControls';
import { World, type WorldSaveData } from './world/World';
import { WATER_LEVEL } from './world/WorldGen';
import { Player } from './player/Player';
import { HOTBAR_SIZE } from './player/Inventory';
import { SaveManager, type GameSave, type WorldMeta, type ProfileData } from './save/SaveManager';
import { CloudSync } from './save/CloudSync';
import { harvest, pickResource, harvestProgress, clearProgress } from './systems/Harvesting';
import { Fishing } from './systems/Fishing';
import { WeatherSystem } from './systems/Weather';
import { CreatureManager } from './entities/CreatureManager';
import { meleeAttack, fireArrow, updateArrows, rollCreatureLoot, type Arrow } from './systems/Combat';
import { QuestSystem } from './systems/Quests';
import { dailyStock, buy, sell, sellableItems, type StockEntry } from './systems/Economy';
import { createTraderPost, shopOpen, type TraderPost } from './entities/Trader';
import { Multiplayer, type MultiplayerSnapshot, type MultiplayerState } from './net/Multiplayer';
import { planPlacement, place, pickStructure, hammerOptions, applyHammer, PlacementGhost, type HammerOption } from './systems/Building';
import { itemDef, maxStack } from './data/items';
import { buildPieces } from './data/build';
import { validateData } from './data';
import type { PlacedStructure } from './world/World';
import { AudioEngine } from './audio/AudioEngine';

/**
 * Oyun orkestrasyonu.
 * Sistemleri birbirine bağlar ama sistemler birbirini tanımaz:
 * haberleşme event bus ve açık arayüzler üzerinden yapılır.
 */

export type GameState = 'boot' | 'menu' | 'loading' | 'playing' | 'paused' | 'dead';

export type GameEvents = {
  'state:changed': { state: GameState };
  toast: { text: string; kind?: 'info' | 'good' | 'bad' };
  'hud:dirty': void;
  'inventory:dirty': void;
  'station:open': { station: 'workbench' | 'furnace' | 'grinder' | 'chest'; structureId: string };
  'hammer:open': { structureId: string; options: HammerOption[] };
  'player:died': { cause: string };
  'save:done': { ok: boolean; error?: string };
  'cloud:status': { status: string; message?: string };
  'cloud:conflict': { worldId: string };
  'achievement': { id: string; label: string };
  'combat:hit': { target: string; killed: boolean };
  'combat:hurt': { by: string; damage: number };
  'shop:open': void;
  'quests:changed': void;
  'mp:status': { status: string; message?: string; players: number };
  'mp:chat': { name: string; text: string };
};

export interface NewWorldOptions {
  name: string;
  mode: 'story' | 'normal';
  difficulty: string;
  seedText: string;
}

const AUTOSAVE_INTERVAL = 30;

export class Game {
  readonly events = new EventBus<GameEvents>();
  readonly settings = new Settings();
  readonly saves = new SaveManager();
  readonly cloud = new CloudSync();
  /** Çok oyunculu sunucu adresi: build sırasında ayarlanabilir. */
  readonly mp = new Multiplayer(import.meta.env.VITE_MP_URL ?? 'https://kg-mp.utkan-gursoy.workers.dev');
  readonly clock = new GameClock(1200);
  readonly input: InputManager;
  readonly touch: TouchControls;
  readonly render: RenderSystem;
  readonly audio = new AudioEngine(this.settings);
  readonly fishing = new Fishing();
  readonly weather = new WeatherSystem();

  state: GameState = 'boot';
  world: World | null = null;
  player: Player | null = null;
  creatures: CreatureManager | null = null;
  quests: QuestSystem | null = null;
  trader: TraderPost | null = null;
  stock: StockEntry[] = [];
  private stockDay = -1;
  meta: WorldMeta | null = null;
  profile: ProfileData;
  achievements = new Set<string>();

  private loop: Loop;
  private ghost: PlacementGhost | null = null;
  private buildRotation = 0;
  private autosaveTimer = 0;
  private attackHeld = 0;
  private lastAttack = 0;
  private attackWasDown = false;
  private powerCooldown = 0;
  private aimedResource: { id: string; name: string; progress: number } | null = null;
  private footstepTimer = 0;
  private wasSwimming = false;
  private observerMode = false;

  get isObserver(): boolean { return this.observerMode; }

  constructor(readonly canvas: HTMLCanvasElement) {
    this.render = new RenderSystem(canvas, this.settings);
    this.input = new InputManager(canvas, this.settings);
    this.touch = new TouchControls(this.input, this.settings);
    this.profile = this.saves.loadProfile();

    // Geliştirme denetimi: veri tutarsızlığı sessizce kalmasın
    const issues = validateData();
    if (issues.length) {
      for (const i of issues) console[i.kind === 'error' ? 'error' : 'warn'](`[data] ${i.where}: ${i.message}`);
    }

    this.loop = new Loop({ fixed: (s) => this.fixed(s), render: (dt) => this.frame(dt) }, 60);
    this.bindInput();

    // Bulut kaydı: başarısız olursa oyun etkilenmez, yerel kayıt sürer.
    this.cloud.events.on('status:changed', ({ status, message }) => this.events.emit('cloud:status', { status, message }));
    this.cloud.events.on('conflict', ({ worldId }) => {
      this.events.emit('cloud:conflict', { worldId });
      this.events.emit('toast', { text: 'Bulutta daha yeni kayıt var. Dünyalarım ekranından seç.', kind: 'bad' });
    });
    this.cloud.events.on('ready', ({ profile }) => {
      // Sunucudaki ad/avatar yerelden yeniyse profili eşitle
      if (profile.name && profile.name !== 'Gezgin' && this.profile.name === 'Gezgin') {
        this.profile.name = profile.name;
        if (profile.avatar) this.profile.avatar = profile.avatar;
        this.saves.saveProfile(this.profile);
      }
    });
    void this.cloud.init();

    this.mp.events.on('status:changed', ({ status, message }) =>
      this.events.emit('mp:status', { status, message, players: this.mp.playerCount }),
    );
    this.mp.events.on('players:changed', ({ count }) =>
      this.events.emit('mp:status', { status: this.mp.status, players: count }),
    );
    this.mp.events.on('world:state', (state) => this.applyMultiplayerState(state));
    this.mp.events.on('world:snapshot', (snapshot) => this.applyMultiplayerSnapshot(snapshot));
    this.mp.events.on('creature:damage-intent', ({ targetId, amount, by }) => {
      if (!this.mp.isHost || !this.creatures || !this.world) return;
      const attacker = this.mp.remotePosition(by);
      const creature = this.creatures.byId(targetId);
      if (!attacker || !creature || attacker.distanceTo(creature.position) > 85) return;
      const result = this.creatures.damage(targetId, Math.max(1, Math.min(200, amount)), true);
      if (result.killed && result.creature) {
        const loot = rollCreatureLoot(this.world, result.creature.def.lootTable, result.creature.id);
        this.mp.sendCreatureReward(by, {
          targetId: result.creature.id,
          name: result.creature.def.name,
          items: loot.items,
          kogox: loot.kogox,
        });
      }
    });
    this.mp.events.on('creature:reward', ({ name, items, kogox }) => {
      const player = this.player;
      if (!player) return;
      const given: Array<{ item: string; count: number }> = [];
      for (const item of items) {
        const left = player.inventory.add(item.item, item.count);
        if (left < item.count) given.push({ item: item.item, count: item.count - left });
      }
      if (kogox > 0) player.kogox += kogox;
      this.events.emit('combat:hit', { target: name, killed: true });
      this.events.emit('toast', { text: `⚔️ ${name} avlandı`, kind: 'good' });
      this.reportLoot(given);
      if (kogox > 0) this.events.emit('toast', { text: `+${kogox} Kogox`, kind: 'good' });
      this.events.emit('inventory:dirty', undefined);
      this.unlock('first_hunt', 'İlk Av');
    });
    this.mp.events.on('chat', (c) => {
      this.events.emit('mp:chat', c);
      this.events.emit('toast', { text: `${c.name}: ${c.text}`, kind: 'info' });
    });
    this.mp.events.on('system', ({ text }) => {
      this.events.emit('toast', { text, kind: 'info' });
    });
  }

  start(): void {
    this.loop.start();
  }

  setState(state: GameState): void {
    if (this.state === state) return;
    this.state = state;
    const playing = state === 'playing';
    this.input.setEnabled(playing);
    this.touch.setVisible(playing && this.settings.isTouch);
    if (!playing) this.input.exitPointerLock();
    this.events.emit('state:changed', { state });
  }

  get fps(): number {
    return this.loop.fps;
  }

  // ------------------------------------------------------------ dünya yaşam döngüsü

  createWorld(opts: NewWorldOptions): void {
    this.observerMode = false;
    const seed = opts.seedText.trim() ? hashString(opts.seedText.trim()) : (Math.random() * 0xffffffff) >>> 0;
    const id = `w${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`;
    const meta: WorldMeta = {
      id,
      name: opts.name.trim() || 'Yeni Dünya',
      mode: opts.mode,
      difficulty: opts.difficulty,
      seed,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      playtime: 0,
      day: 1,
    };
    this.setState('loading');
    this.teardownWorld();

    const world = new World({ seed, mode: opts.mode, difficulty: opts.difficulty, name: meta.name }, this.settings);
    const player = new Player(this.render.camera);
    this.attach(world, player, meta);

    // Uygun doğma noktası: su üstü, aşırı dik olmayan yer
    const spawn = this.findSpawn(world);
    player.placeAt(world, spawn.x, spawn.z);
    player.stats.reset();
    // Başlangıç eşyaları: oyuncu ilk dakikada ne yapacağını bilsin
    player.inventory.add('wood_axe', 1);
    player.inventory.add('wood_pickaxe', 1);
    player.inventory.add('bread', 3);

    this.clock.load({ elapsed: 8 * (this.clock.dayLength / 24), day: 1 }); // sabah 08:00
    this.trader = createTraderPost(world, spawn.x, spawn.z);
    this.achievements = new Set();
    this.profile.stats.worldsCreated++;
    this.saves.saveProfile(this.profile);

    this.saveNow();
    this.setState('playing');
    this.events.emit('toast', { text: `${meta.name} oluşturuldu`, kind: 'good' });
  }

  loadWorld(id: string): boolean {
    this.observerMode = false;
    const res = this.saves.load(id);
    if (!res.save) {
      this.events.emit('toast', { text: res.error ?? 'Kayıt yüklenemedi', kind: 'bad' });
      return false;
    }
    this.setState('loading');
    this.teardownWorld();

    const save = res.save;
    const world = new World(
      { seed: save.world.seed, mode: save.world.mode, difficulty: save.world.difficulty, name: save.meta.name },
      this.settings,
    );
    world.loadSave(save.world);
    const player = new Player(this.render.camera);
    this.attach(world, player, save.meta);
    player.loadSave(save.player, world);
    // Eski kayıtlarda seçili hotbar yuvası boş kalabiliyor. Elde kullanılabilir
    // bir araç varken onu otomatik seçerek oyuncunun boş elle açılmasını önle.
    if (!player.inventory.held() || player.inventory.held()?.broken || !itemDef(player.inventory.held()!.item).tool) {
      const toolIndex = player.inventory.slots.findIndex((slot, index) => index < 8 && !!slot && !slot.broken && !!itemDef(slot.item).tool);
      if (toolIndex >= 0) player.inventory.select(toolIndex);
    }
    this.clock.load(save.time);
    this.weather.load(save.weather);
    const tSpawn = this.findSpawn(world);
    this.trader = createTraderPost(world, tSpawn.x, tSpawn.z);
    this.quests?.loadSave(save.quests);
    this.achievements = new Set(save.achievements ?? []);

    if (res.recovered) this.events.emit('toast', { text: 'Kayıt yedekten geri yüklendi', kind: 'info' });
    this.setState('playing');
    return true;
  }

  private attach(world: World, player: Player, meta: WorldMeta): void {
    this.world = world;
    this.player = player;
    this.meta = meta;
    this.render.attach(world.scene);
    this.ghost = new PlacementGhost(world.scene);
    this.creatures = new CreatureManager(world, this.clock);
    this.quests = new QuestSystem(world);
    this.bindWorldRuntime(world);

    player.stats.events.on('died', ({ cause }) => {
      if (this.observerMode) return;
      this.profile.stats.deaths++;
      this.saves.saveProfile(this.profile);
      this.applyDeathPenalty(world, player);
      this.setState('dead');
      this.events.emit('player:died', { cause });
      this.audio.playerDeath();
    });
    player.stats.events.on('damaged', () => this.audio.playerHurt());
    player.inventory.events.on('changed', () => this.events.emit('inventory:dirty', undefined));
    player.inventory.events.on('tool:broken', ({ item }) => {
      this.events.emit('toast', { text: `${itemDef(item).name} kırıldı`, kind: 'bad' });
      this.audio.toolBroken();
    });
  }

  /** World/creature nesnesi snapshot nedeniyle değişse de aynı oyun event bağlarını kurar. */
  private bindWorldRuntime(world: World): void {
    this.creatures?.events.on('killed', ({ def, byPlayer }) => {
      if (this.observerMode) return;
      if (byPlayer) this.quests?.track('hunt', def);
    });
    this.creatures?.events.on('killed', ({ def }) => {
      if (this.observerMode) return;
      this.profile.stats.playtime = this.profile.stats.playtime;
      this.audio.creatureDeath();
      void def;
    });
    world.events.on('chunk:discovered', ({ total }) => {
      if (this.observerMode) return;
      this.profile.stats.discovered = Math.max(this.profile.stats.discovered, total);
      if (total >= 40) this.unlock('explorer', 'Büyük Keşif');
    });
  }

  /**
   * Ölüm cezası (zorluğa göre):
   *  - 'keep': hiçbir şey düşürülmez.
   *  - 'dropHotbar': hızlı erişim (hotbar) yuvaları düşürülür, çanta korunur.
   *  - 'dropAll': envanter VE kuşanılan ekipman tamamen düşürülür.
   * Düşen eşyalar yok edilmez: ölüm konumuna bir "mezar sandığı" olarak
   * konur, oyuncu geri dönüp alabilir (adil ceza — sonsuza dek kaybolmaz).
   */
  private applyDeathPenalty(world: World, player: Player): void {
    const penalty = world.params.difficulty.deathPenalty;
    if (penalty === 'keep') return;

    const dropped: Array<{ item: string; count: number; dur?: number }> = [];
    const inv = player.inventory;

    if (penalty === 'dropHotbar') {
      for (let i = 0; i < HOTBAR_SIZE; i++) {
        const s = inv.slots[i];
        if (s) dropped.push({ item: s.item, count: s.count, dur: s.dur });
        inv.slots[i] = null;
      }
    } else if (penalty === 'dropAll') {
      for (let i = 0; i < inv.slots.length; i++) {
        const s = inv.slots[i];
        if (s) dropped.push({ item: s.item, count: s.count, dur: s.dur });
        inv.slots[i] = null;
      }
      for (const key of ['head', 'chest', 'legs', 'feet'] as const) {
        const s = inv.equipment[key];
        if (s) dropped.push({ item: s.item, count: s.count, dur: s.dur });
        inv.equipment[key] = null;
      }
    }
    inv.events.emit('changed', undefined);

    if (dropped.length === 0) return;

    const graveDef = buildPieces.require('chest');
    const gx = Math.round(player.position.x / 2) * 2;
    const gz = Math.round(player.position.z / 2) * 2;
    const gy = world.supportHeightAt(gx, gz, player.position.y);
    const res = world.apply({
      t: 'structure:place',
      piece: graveDef.id,
      x: gx,
      y: gy,
      z: gz,
      rotY: 0,
      storageSize: Math.max(24, dropped.length),
    });
    if (!res.ok || !res.data) {
      // Aynı hücrede yapı varsa mezar kurulamaz: eşyayı kaybetmek yerine geri ver
      for (const d of dropped) inv.add(d.item, d.count, d.dur);
      this.events.emit('toast', { text: 'Eşyalarını bırakacak yer yoktu, çantanda kaldı.', kind: 'info' });
      return;
    }
    const grave = res.data as PlacedStructure;
    if (grave.storage) {
      dropped.forEach((d, i) => {
        if (i < grave.storage!.length) grave.storage![i] = { item: d.item, count: d.count, dur: d.dur };
      });
    }
    this.events.emit('toast', {
      text: penalty === 'dropAll' ? 'Öldün: tüm eşyaların mezar sandığına düştü.' : 'Öldün: hızlı erişim eşyaların mezar sandığına düştü.',
      kind: 'bad',
    });
  }

  private teardownWorld(): void {
    this.mp.disconnect();
    clearProgress();
    this.trader = null;
    this.quests = null;
    this.stock = [];
    this.stockDay = -1;
    this.arrows.length = 0;
    if (this.creatures) {
      this.creatures.dispose();
      this.creatures = null;
    }
    if (this.world) {
      this.world.dispose();
      this.world = null;
    }
    // Player'ın kamera altındaki birinci-şahıs viewmodeli de eski dünyayla
    // birlikte kaldırılmalı; aksi halde dünya değişiminde hayalet alet kalır.
    this.player?.dispose();
    this.player = null;
    this.ghost = null;
  }

  exitToMenu(): void {
    if (!this.observerMode) this.saveNow({ flush: true });
    this.teardownWorld();
    this.observerMode = false;
    this.setState('menu');
  }

  /**
   * Doğma noktası seçimi.
   * Yalnızca "kara mı" diye bakmak yetmiyor: oyuncu minik bir kıyı şeridinde
   * doğabiliyordu. Çevresinde yeterli kara olan, düz ve su seviyesinden
   * belirgin yükseklikte bir yer aranır.
   */
  private findSpawn(world: World): { x: number; z: number } {
    let fallback: { x: number; z: number } | null = null;
    for (let i = 0; i < 600; i++) {
      const angle = (i / 600) * Math.PI * 2 * 7;
      const r = 20 + i * 3;
      const x = Math.cos(angle) * r;
      const z = Math.sin(angle) * r;
      const h = world.heightAt(x, z);
      if (h <= WATER_LEVEL + 1.5 || h > 30) continue;
      if (world.slopeAt(x, z) > 0.6) continue;
      if (!fallback) fallback = { x, z };
      // Çevrede kara oranı: 8 yönde 40 birim
      let land = 0;
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2;
        if (world.heightAt(x + Math.cos(a) * 40, z + Math.sin(a) * 40) > WATER_LEVEL + 0.5) land++;
      }
      if (land >= 6) return { x, z };
    }
    return fallback ?? { x: 0, z: 0 };
  }

  // ------------------------------------------------------------ kayıt

  buildSave(): GameSave | null {
    if (!this.world || !this.player || !this.meta) return null;
    this.meta.playtime = this.player.playtime;
    this.meta.day = this.clock.day;
    return {
      version: 0, // SaveManager doldurur
      meta: this.meta,
      world: this.world.toSave() as WorldSaveData,
      player: this.player.toSave(),
      time: this.clock.toJSON(),
      weather: this.weather.toSave(),
      quests: this.quests?.toSave() ?? null,
      economy: null,
      achievements: [...this.achievements],
    };
  }

  saveNow(opts: { flush?: boolean } = {}): void {
    if (this.observerMode) return;
    const save = this.buildSave();
    if (!save) return;
    // 1) Yerel kayıt her zaman ve önce: bulut olmasa da veri güvende
    const res = this.saves.save(save);
    this.events.emit('save:done', res.ok ? { ok: true } : { ok: false, error: res.error });
    if (!res.ok) this.events.emit('toast', { text: `Kayıt hatası: ${res.error}`, kind: 'bad' });
    this.profile.stats.playtime = Math.max(this.profile.stats.playtime, this.player?.playtime ?? 0);
    this.saves.saveProfile(this.profile);

    // 2) Bulut: kotayı korumak için kısıtlı aralıkla, çıkışta zorlanır
    if (opts.flush) {
      void this.cloud.push(save, { throttle: false });
      void this.cloud.updateProfile({ name: this.profile.name, stats: this.profile.stats, achievements: this.profile.achievements });
    } else {
      void this.cloud.push(save);
    }
  }

  /** Sekme kapanırken çağrılır: bekleyen bulut kaydını yollar. */
  flushOnExit(): void {
    if (this.observerMode) return;
    const save = this.buildSave();
    if (!save) return;
    this.saves.save(save);
    this.cloud.flushOnExit(save);
  }

  /** Login/kurtarma sonrası bu hesaba ait bulut dünyalarını otomatik olarak
   * cihazın güvenli yerel kayıt alanına indirir. Yerelde zaten bulunan aynı
   * dünya asla ezilmez; çakışma çözümü kullanıcıya bırakılır. */
  async restoreCloudWorldsAfterLogin(): Promise<{ restored: number; available: number }> {
    if (this.observerMode) return { restored: 0, available: 0 };
    const worlds = await this.cloud.list();
    let restored = 0;
    for (const world of worlds) {
      if (this.saves.hasWorld(world.id)) continue;
      const save = await this.cloud.pull(world.id);
      if (!save) continue;
      if (this.saves.save(save).ok) restored++;
    }
    if (restored > 0) {
      this.events.emit('toast', { text: `${restored} dünya hesabından geri yüklendi.`, kind: 'good' });
    } else if (worlds.length === 0) {
      this.events.emit('toast', { text: 'Bu hesapta henüz bulut dünyası yok.', kind: 'info' });
    }
    return { restored, available: worlds.length };
  }

  /** Buluttan bir dünyayı yerele indirip açar. */
  async loadFromCloud(worldId: string): Promise<boolean> {
    if (this.observerMode) return false;
    const save = await this.cloud.pull(worldId);
    if (!save) {
      this.events.emit('toast', { text: 'Buluttan indirilemedi', kind: 'bad' });
      return false;
    }
    const res = this.saves.save(save);
    if (!res.ok) {
      this.events.emit('toast', { text: `Yerele yazılamadı: ${res.error}`, kind: 'bad' });
      return false;
    }
    return this.loadWorld(worldId);
  }

  /** Çakışmayı çöz: 'cloud' buluttakini alır, 'local' yereli zorla gönderir. */
  async resolveCloudConflict(worldId: string, choice: 'cloud' | 'local'): Promise<void> {
    if (this.observerMode) return;
    if (choice === 'local') {
      const res = this.saves.load(worldId);
      if (res.save) await this.cloud.resolveConflict(worldId, 'local', res.save);
      this.events.emit('toast', { text: 'Yerel kayıt buluta yazıldı', kind: 'good' });
      return;
    }
    const save = await this.cloud.resolveConflict(worldId, 'cloud');
    if (save) {
      this.saves.save(save);
      this.events.emit('toast', { text: 'Buluttaki kayıt alındı', kind: 'good' });
    }
  }

  unlock(id: string, label: string): void {
    if (this.achievements.has(id)) return;
    this.achievements.add(id);
    if (!this.profile.achievements.includes(id)) {
      this.profile.achievements.push(id);
      this.saves.saveProfile(this.profile);
    }
    this.events.emit('achievement', { id, label });
    this.events.emit('toast', { text: `🏆 ${label}`, kind: 'good' });
    this.audio.achievement();
  }

  // ------------------------------------------------------------ girdi bağlamaları

  private bindInput(): void {
    this.input.events.on('hotbar', ({ index }) => {
      if (this.observerMode) return;
      this.player?.inventory.select(index);
    });
    this.input.events.on('wheel', ({ delta }) => {
      if (this.observerMode) return;
      this.player?.inventory.cycle(delta);
    });
    this.input.events.on('released', ({ action }) => {
      if (action === 'attack') this.releaseAttack();
    });
    this.input.events.on('pressed', ({ action }) => {
      if (this.state !== 'playing') return;
      if (this.observerMode && !['forward', 'back', 'left', 'right', 'sprint', 'crouch', 'jump', 'menu'].includes(action)) return;
      if (action === 'jump') this.player?.requestJump();
      if (action === 'interact') this.interact();
      if (action === 'power') this.usePower();
    });
  }

  /** Oyuncunun bulunduğu biyoma/suya göre ayak sesi zemini. */
  private surfaceUnderPlayer(): 'grass' | 'stone' | 'sand' | 'wood' | 'water' | 'ice' {
    const { world, player } = this;
    if (!world || !player) return 'grass';
    if (player.swimming) return 'water';
    const biome = world.biomeAt(player.position.x, player.position.z).biome;
    if (biome.id === 'beach') return 'sand';
    if (biome.id === 'mountain') return 'stone';
    if (biome.id === 'ice') return 'ice';
    return 'grass';
  }

  /** Adım/zıplama/iniş/su sıçraması ve ortam (biyom+gece) seslerini yönetir. */
  private updateAmbientAudio(step: number): void {
    const { world, player } = this;
    if (!world || !player) return;

    const biome = world.biomeAt(player.position.x, player.position.z).biome;
    this.audio.setAmbient(biome.ambient, this.clock.isNight);
    this.audio.updateAmbientDetail(step, biome.ambient, this.clock.isNight);

    if (player.swimming && !this.wasSwimming) this.audio.splash();
    this.wasSwimming = player.swimming;

    if (player.justJumped) this.audio.jump();
    const landed = player.justLanded;
    if (landed) this.audio.land(this.surfaceUnderPlayer());

    if (player.moving && player.grounded && !player.swimming && !landed) {
      this.footstepTimer -= step;
      if (this.footstepTimer <= 0) {
        this.footstepTimer = player.sprinting ? 0.28 : 0.42;
        this.audio.footstep(this.surfaceUnderPlayer());
      }
    } else if (!player.moving && this.footstepTimer < 0) {
      this.footstepTimer = 0;
    }
  }

  /** Elindeki eşya yerleştirilebilir mi? */
  private heldPiece(): string | null {
    const held = this.player?.inventory.held();
    if (!held) return null;
    const def = itemDef(held.item);
    return def.place?.piece ?? null;
  }

  private interact(): void {
    if (this.observerMode) return;
    const { world, player } = this;
    if (!world || !player) return;

    // Aktif oltada E/etkileşim: ısırdıysa sar, değilse iptal et.
    if (this.fishing.active) {
      const catchResult = this.fishing.reel(player, this.clock.isNight, this.weather.kind);
      if (catchResult) this.finishCatch(catchResult.fish.id, catchResult.fish.name);
      else this.events.emit('toast', { text: 'Olta toplandı', kind: 'info' });
      return;
    }

    // 1) Çekiç elde ise yapı düzenleme menüsü
    const held = player.inventory.held();
    const tool = held ? itemDef(held.item).tool : null;
    if (tool?.class === 'hammer') {
      const st = pickStructure(world, player);
      if (st) {
        this.events.emit('hammer:open', { structureId: st.id, options: hammerOptions(world, st) });
        this.audio.uiClick();
        return;
      }
    }

    // 2) BEDO: yakınsa dükkân/görev ekranı
    if (this.trader) {
      const d = Math.hypot(player.position.x - this.trader.x, player.position.z - this.trader.z);
      if (d < 6) {
        if (!shopOpen(this.clock.hour)) {
          this.events.emit('toast', { text: 'BEDO: Dükkân kapalı patron, sabah gel.', kind: 'info' });
          return;
        }
        this.refreshStock();
        this.events.emit('shop:open', undefined);
        return;
      }
    }

    // 3) İstasyon / sandık aç
    const st = pickStructure(world, player);
    if (st) {
      const def = buildPieces.get(st.piece);
      if (def?.shape === 'station') {
        const kind = st.piece as 'workbench' | 'furnace' | 'grinder' | 'chest';
        this.events.emit('station:open', { station: kind, structureId: st.id });
        if (kind === 'chest') this.audio.chestOpen();
        else this.audio.uiClick();
        return;
      }
    }
    this.events.emit('toast', { text: 'Etkileşilecek bir şey yok', kind: 'info' });
  }

  applyHammerOption(structureId: string, option: HammerOption): void {
    if (this.observerMode) return;
    const { world, player } = this;
    if (!world || !player) return;
    const st = world.structureById(structureId);
    if (!st) return;
    const res = applyHammer(world, player, st, option);
    this.events.emit('toast', { text: res.ok ? res.message : res.reason, kind: res.ok ? 'good' : 'bad' });
    if (res.ok) this.audio.hammerUpgrade();
  }

  /** Sandık yuvasına oyuncunun envanterindeki bir yuvanın TAMAMINI taşır. */
  chestDeposit(structureId: string, invIndex: number, chestSlot: number): boolean {
    if (this.observerMode) return false;
    const { world, player } = this;
    if (!world || !player) return false;
    const st = world.structureById(structureId);
    const source = player.inventory.slots[invIndex];
    if (!st || !st.storage || !source) return false;
    if (chestSlot < 0 || chestSlot >= st.storage.length || st.storage[chestSlot]) return false;
    const res = world.apply({ t: 'chest:store', id: structureId, slot: chestSlot, item: source.item, count: source.count, dur: source.dur });
    if (!res.ok) return false;
    player.inventory.slots[invIndex] = null;
    player.inventory.events.emit('changed', undefined);
    this.audio.uiClick();
    return true;
  }

  /** Sandık içinde iki yuva arasında taşıma; aynı eşyaysa yığınları birleştirir. */
  chestSwap(structureId: string, a: number, b: number): boolean {
    if (this.observerMode) return false;
    const world = this.world;
    const st = world?.structureById(structureId);
    if (!world || !st || !st.storage) return false;
    const A = st.storage[a];
    const B = st.storage[b];
    if (!A) return false;

    if (B && A.item === B.item && A.dur === undefined && B.dur === undefined) {
      const max = maxStack(A.item);
      const move = Math.min(max - B.count, A.count);
      if (move > 0) {
        const newB = B.count + move;
        const newACount = A.count - move;
        world.apply({ t: 'chest:store', id: structureId, slot: b, item: A.item, count: newB });
        world.apply({ t: 'chest:store', id: structureId, slot: a, item: newACount > 0 ? A.item : null, count: newACount, dur: A.dur });
        return true;
      }
    }
    // Basit takas
    world.apply({ t: 'chest:store', id: structureId, slot: a, item: B?.item ?? null, count: B?.count ?? 0, dur: B?.dur });
    world.apply({ t: 'chest:store', id: structureId, slot: b, item: A.item, count: A.count, dur: A.dur });
    return true;
  }

  /** Sandık yuvasındaki eşyayı oyuncunun envanterine (varsa istenen yuvaya) alır. */
  chestWithdraw(structureId: string, chestSlot: number, invIndex?: number): boolean {
    if (this.observerMode) return false;
    const { world, player } = this;
    if (!world || !player) return false;
    const st = world.structureById(structureId);
    const item = st?.storage?.[chestSlot];
    if (!st || !st.storage || !item) return false;

    if (invIndex !== undefined) {
      if (player.inventory.slots[invIndex]) return false; // hedef yuva dolu
      const res = world.apply({ t: 'chest:store', id: structureId, slot: chestSlot, item: null, count: 0 });
      if (!res.ok) return false;
      player.inventory.slots[invIndex] = { item: item.item, count: item.count, dur: item.dur };
      player.inventory.events.emit('changed', undefined);
      this.audio.uiClick();
      return true;
    }

    // Yuva belirtilmediyse: yığınlanabiliyorsa mevcut yığına, yoksa ilk boş yuvaya
    const left = player.inventory.add(item.item, item.count, item.dur);
    if (left >= item.count) return false; // hiç yer yok
    const res = world.apply({
      t: 'chest:store',
      id: structureId,
      slot: chestSlot,
      item: left > 0 ? item.item : null,
      count: left,
      dur: item.dur,
    });
    if (res.ok) this.audio.uiClick();
    return res.ok;
  }

  private usePower(): void {
    if (this.observerMode) return;
    const player = this.player;
    if (!player) return;
    if (!player.inventory.hasFullSet('power')) {
      this.events.emit('toast', { text: 'ÖZ GÜÇ için 4 parça Güç Zırhı gerekir', kind: 'info' });
      return;
    }
    if (this.powerCooldown > 0) {
      this.events.emit('toast', { text: `Bekleme: ${this.powerCooldown.toFixed(1)}s`, kind: 'info' });
      return;
    }
    this.powerCooldown = 10;
    this.spawnEnergyBolt();
    this.events.emit('toast', { text: 'ÖZ GÜÇ!', kind: 'good' });
  }

  /** Beyaz enerji topu: 50 hasar, 10 saniye bekleme. */
  private bolts: Array<{ mesh: THREE.Mesh; dir: THREE.Vector3; life: number }> = [];
  private spawnEnergyBolt(): void {
    const { world, player } = this;
    if (!world || !player) return;
    const geo = new THREE.SphereGeometry(0.35, 10, 8);
    const mat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(player.eyePosition);
    const light = new THREE.PointLight(0xdfefff, 2.2, 14);
    mesh.add(light);
    world.scene.add(mesh);
    this.bolts.push({ mesh, dir: player.forward(), life: 2.4 });
  }

  get powerCooldownRemaining(): number {
    return this.powerCooldown;
  }

  get aimInfo(): { name: string; progress: number } | null {
    if (this.fishing.label) return { name: this.fishing.label, progress: this.fishing.state === 'bite' ? 1 : 0.45 };
    return this.aimedResource ? { name: this.aimedResource.name, progress: this.aimedResource.progress } : null;
  }

  // ------------------------------------------------------------ döngü

  private fixed(step: number): void {
    if (this.state !== 'playing') return;
    const { world, player } = this;
    if (!world || !player) return;

    const authoritative = !this.mp.connected || this.mp.isHost;
    const tick = authoritative ? this.clock.update(step) : { newDay: false };
    if (authoritative && this.weather.update(step)) this.events.emit('toast', { text: `Hava değişti: ${this.weather.label}`, kind: 'info' });
    if (tick.newDay) {
      this.refreshStock();
      this.events.emit('toast', { text: `Gün ${this.clock.day} başladı. BEDO'da yeni görevler var.`, kind: 'info' });
      this.events.emit('quests:changed', undefined);
      this.audio.dayStart();
    }
    player.update(step, world, this.input);
    this.updateAmbientAudio(step);
    if (this.observerMode) {
      this.creatures?.updatePresentation(step);
      return;
    }
    const fishingEvent = this.fishing.update(step);
    if (fishingEvent === 'bite') { this.audio.splash(); this.events.emit('toast', { text: '🐟 Isırdı! E ile sar!', kind: 'good' }); }
    else if (fishingEvent === 'missed') this.events.emit('toast', { text: 'Balık kaçtı', kind: 'info' });

    // Saldırı/toplama.
    // İlk basış ANINDA iş yapar; basılı tutuldukça alet hızına göre tekrarlar.
    // (Aksi halde tek dokunuşlar hiç tetiklenmiyor ve oyun tepkisiz hissettiriyor.)
    if (this.input.isDown('attack')) {
      if (!this.attackWasDown) {
        this.attackWasDown = true;
        this.attackHeld = 0;
        this.lastAttack = 0;
        this.doPrimary();
      } else {
        this.attackHeld += step;
        const held = player.inventory.held();
        const tool = held ? itemDef(held.item).tool : null;
        const interval = 1 / Math.max(0.7, (tool?.speed ?? 1) * 1.6);
        if (this.attackHeld - this.lastAttack >= interval) {
          this.lastAttack = this.attackHeld;
          this.doPrimary();
        }
      }
    } else {
      this.attackWasDown = false;
      this.attackHeld = 0;
      this.lastAttack = 0;
    }

    if (this.powerCooldown > 0) this.powerCooldown = Math.max(0, this.powerCooldown - step);

    // Canlılar: AI, saldırı ve oyuncuya hasar
    if (this.creatures && authoritative) {
      const noisy = this.input.isDown('sprint') || this.input.isDown('attack');
      this.creatures.update(step, player.position, noisy, (dmg, by, inflicts) => {
        player.stats.damage(dmg, creatureLabel(by), player.inventory.defense());
        if (inflicts) player.stats.addEffect({ id: inflicts.effect, magnitude: inflicts.magnitude, duration: inflicts.duration });
        this.events.emit('combat:hurt', { by, damage: dmg });
      });
    } else if (this.creatures) {
      this.creatures.updatePresentation(step);
    }

    // Uçan oklar
    if (this.creatures && this.arrows.length > 0) {
      updateArrows(
        this.arrows,
        step,
        world,
        this.creatures,
        (name, killed, loot, targetId, damage) => {
          if (!authoritative) this.mp.sendCreatureDamageIntent(targetId, damage);
          this.events.emit('combat:hit', { target: name, killed });
          if (killed) {
            this.events.emit('toast', { text: `🏹 ${name} avlandı`, kind: 'good' });
            this.reportLoot(loot);
          }
        },
        player,
        authoritative,
      );
    }

    // Yay gerilmesi
    if (this.drawingBow) this.bowDraw = Math.min(1, this.bowDraw + step / 0.85);

    // Enerji topları
    for (let i = this.bolts.length - 1; i >= 0; i--) {
      const b = this.bolts[i]!;
      b.mesh.position.addScaledVector(b.dir, 42 * step);
      b.life -= step;
      const ground = world.heightAt(b.mesh.position.x, b.mesh.position.z);
      if (b.life <= 0 || b.mesh.position.y < ground) {
        world.scene.remove(b.mesh);
        b.mesh.geometry.dispose();
        (b.mesh.material as THREE.Material).dispose();
        this.bolts.splice(i, 1);
      }
    }

    this.autosaveTimer += step;
    if (this.autosaveTimer >= AUTOSAVE_INTERVAL) {
      this.autosaveTimer = 0;
      this.saveNow();
    }
  }

  /** Sol tık / saldırı butonu: yerleştir ya da topla. */
  private doPrimary(): void {
    if (this.observerMode) return;
    const { world, player } = this;
    if (!world || !player) return;

    const pieceId = this.heldPiece();
    if (pieceId) {
      const plan = planPlacement(world, player, pieceId, this.buildRotation);
      if (plan) {
        const res = place(world, player, plan);
        this.events.emit('toast', {
          text: res.ok ? `${plan.piece.name} kuruldu` : res.reason,
          kind: res.ok ? 'good' : 'bad',
        });
        if (res.ok) {
          this.unlock('first_build', 'İlk Yapı');
          this.audio.buildPlace();
        }
      }
      return;
    }

    // Yay elde ise gerilmeye başlar (bırakınca atılır)
    const heldItem = player.inventory.held();
    const heldTool = heldItem ? itemDef(heldItem.item).tool : null;
    if (heldTool?.class === 'bow') {
      this.drawingBow = true;
      this.audio.bowDrawStart();
      return;
    }
    if (heldTool?.class === 'rod') {
      const cast = this.fishing.cast(world, player);
      if (cast.ok) this.audio.splash();
      else if (cast.message) this.events.emit('toast', { text: cast.message, kind: 'info' });
      return;
    }

    // Olgun ekinler doğrudan hasat edilir.
    const crop = pickStructure(world, player);
    if (crop && (crop.piece === 'crop_wheat' || crop.piece === 'crop_berry')) {
      const result = world.harvestCrop(crop.id, this.clock.elapsed);
      if (!result.ok) { this.events.emit('toast', { text: result.reason ?? '', kind: 'info' }); return; }
      const item = result.piece === 'crop_wheat' ? 'wheat' : 'berry';
      const seed = result.piece === 'crop_wheat' ? 'wheat_seed' : 'berry_seed';
      const left = player.inventory.add(item, 3);
      player.inventory.add(seed, 1);
      this.events.emit('inventory:dirty', undefined);
      this.events.emit('toast', { text: left ? 'Çanta dolu' : `+ ${itemDef(item).name} x3`, kind: left ? 'bad' : 'good' });
      this.audio.gather();
      return;
    }

    // Önce canlıya vur: nişan konisindeki hedef varsa saldırı olur
    if (this.creatures) {
      const authoritative = !this.mp.connected || this.mp.isHost;
      const atk = meleeAttack(world, player, this.creatures, authoritative);
      player.triggerSwing();
      if (atk.hit) {
        if (!authoritative && atk.targetId && atk.damage) this.mp.sendCreatureDamageIntent(atk.targetId, atk.damage);
        this.audio.hitCreature();
        this.events.emit('combat:hit', { target: atk.targetName ?? '', killed: !!atk.killed });
        if (atk.killed) {
          this.events.emit('toast', { text: `⚔️ ${atk.targetName} avlandı`, kind: 'good' });
          this.reportLoot(atk.loot);
          if (atk.kogox) this.events.emit('toast', { text: `+${atk.kogox} Kogox`, kind: 'good' });
          this.unlock('first_hunt', 'İlk Av');
        }
        return;
      }
      this.audio.attackSwing();
    }

    const fb = harvest(world, player, this.clock.elapsed);
    if (fb.kind === 'hit') {
      this.audio.gatherSoundFor(fb.target?.def.kind);
      player.triggerSwing();
    } else if (fb.kind === 'broken') {
      player.triggerSwing();
      const names = (fb.drops ?? []).map((d) => `${itemDef(d.item).name} x${d.count}`).join(', ');
      if (names) this.events.emit('toast', { text: `+ ${names}`, kind: 'good' });
      this.audio.resourceBreak();
      if (fb.target?.def.kind === 'ore') {
        this.profile.stats.oresMined++;
        this.unlock('first_ore', 'İlk Maden');
      }
      if (fb.message) this.events.emit('toast', { text: fb.message, kind: 'info' });
    } else if (fb.kind === 'blocked' || fb.kind === 'full') {
      this.events.emit('toast', { text: fb.message ?? '', kind: 'bad' });
    }
  }

  private arrows: Arrow[] = [];
  private drawingBow = false;
  private bowDraw = 0;

  get bowDrawRatio(): number {
    return this.drawingBow ? this.bowDraw : 0;
  }

  /** Saldırı bırakıldığında: yay gerilmişse ok atar. */
  releaseAttack(): void {
    if (this.observerMode) return;
    if (!this.drawingBow) return;
    const { world, player } = this;
    this.drawingBow = false;
    const ratio = this.bowDraw;
    this.bowDraw = 0;
    if (!world || !player) return;
    if (ratio < 0.15) return;
    const arrow = fireArrow(world, player, ratio);
    if (arrow) {
      this.arrows.push(arrow);
      this.audio.bowRelease(ratio);
    } else this.events.emit('toast', { text: 'Ok yok!', kind: 'bad' });
  }

  private finishCatch(id: string, name: string): void {
    this.profile.stats.fishCaught++;
    this.quests?.track('fish', id);
    this.events.emit('inventory:dirty', undefined);
    this.events.emit('quests:changed', undefined);
    this.events.emit('toast', { text: `🎣 ${name} yakalandı!`, kind: 'good' });
    this.audio.gather();
    this.unlock('first_fish', 'İlk Balık');
  }

  private reportLoot(loot?: Array<{ item: string; count: number }>): void {
    if (!loot || loot.length === 0) return;
    const text = loot.map((d) => `${itemDef(d.item).name} x${d.count}`).join(', ');
    this.events.emit('toast', { text: `+ ${text}`, kind: 'good' });
  }

  /** Günlük stok ve görevleri güncel tut. */
  refreshStock(): void {
    const world = this.world;
    if (!world) return;
    if (this.stockDay !== this.clock.day) {
      this.stockDay = this.clock.day;
      this.stock = dailyStock(world, this.clock.day);
    }
    this.quests?.ensureDay(this.clock.day, world.discoveredCount);
  }

  buyFromShop(index: number, count = 1): void {
    if (this.observerMode) return;
    const world = this.world;
    const player = this.player;
    const entry = this.stock[index];
    if (!world || !player || !entry) return;
    const res = buy(world, player, entry, count);
    this.events.emit('toast', { text: res.ok ? res.message : res.reason, kind: res.ok ? 'good' : 'bad' });
    if (res.ok) this.audio.coin();
  }

  sellToShop(itemId: string, count = 1): void {
    if (this.observerMode) return;
    const world = this.world;
    const player = this.player;
    if (!world || !player) return;
    const res = sell(world, player, itemId, count);
    if (res.ok) {
      this.profile.stats.kogoxEarned += 1;
      this.unlock('first_sale', 'İlk Satış');
      this.audio.coin();
    }
    this.events.emit('toast', { text: res.ok ? res.message : res.reason, kind: res.ok ? 'good' : 'bad' });
  }

  sellList(): Array<{ item: string; count: number; unit: number }> {
    const world = this.world;
    const player = this.player;
    if (!world || !player) return [];
    return sellableItems(world, player);
  }

  completeQuest(questId: string): void {
    if (this.observerMode) return;
    const player = this.player;
    const world = this.world;
    if (!this.quests || !player || !world) return;
    const q = this.quests.state.quests.find((x) => x.id === questId);
    if (!q) return;
    const res = this.quests.complete(q, player, world.discoveredCount);
    this.events.emit('toast', { text: res.message, kind: res.ok ? 'good' : 'bad' });
    if (res.ok) {
      this.events.emit('quests:changed', undefined);
      this.audio.questDone();
      if (player.kogox >= 1000) this.unlock('kogox_1000', '1000 Kogox');
    }
  }

  private multiplayerState(): MultiplayerState {
    return {
      creatures: this.creatures?.toSnapshot() ?? [],
      time: this.clock.toJSON(),
      weather: this.weather.toSave(),
    };
  }

  private multiplayerSnapshot(): MultiplayerSnapshot {
    if (!this.world) throw new Error('Dünya yüklenmedi');
    return {
      version: 1,
      world: this.world.toSave(),
      ...this.multiplayerState(),
      time: this.clock.toJSON(),
      weather: this.weather.toSave(),
    };
  }

  private applyMultiplayerState(state: MultiplayerState): void {
    if (this.mp.isHost || !Array.isArray(state.creatures)) return;
    this.creatures?.applySnapshot(state.creatures);
    if (state.time) this.clock.load(state.time);
    if (state.weather) this.weather.load(state.weather);
  }

  private applyMultiplayerSnapshot(snapshot: MultiplayerSnapshot): void {
    if (this.mp.isHost || snapshot.version !== 1 || !snapshot.world || !Array.isArray(snapshot.creatures)) return;
    const current = this.world;
    const differentWorld = !current || current.params.seed !== snapshot.world.seed || current.params.mode !== snapshot.world.mode || current.params.difficulty.id !== snapshot.world.difficulty;
    if (differentWorld) {
      const player = this.player;
      this.creatures?.dispose();
      current?.dispose();
      const world = new World(snapshot.world, this.settings);
      world.loadSave(snapshot.world);
      this.world = world;
      this.render.attach(world.scene);
      this.ghost = new PlacementGhost(world.scene);
      this.creatures = new CreatureManager(world, this.clock);
      this.quests = new QuestSystem(world);
      this.bindWorldRuntime(world);
      const traderSpawn = this.findSpawn(world);
      this.trader = createTraderPost(world, traderSpawn.x, traderSpawn.z);
      if (this.meta) {
        this.meta.seed = snapshot.world.seed;
        this.meta.mode = snapshot.world.mode;
        this.meta.difficulty = snapshot.world.difficulty;
        this.meta.name = snapshot.world.name;
      }
      if (player) {
        const y = world.heightAt(player.position.x, player.position.z);
        player.position.y = Math.max(player.position.y, y + 0.1);
      }
      this.mp.rebindWorld(world);
    } else {
      current.loadSave(snapshot.world);
    }
    this.clock.load(snapshot.time);
    this.weather.load(snapshot.weather);
    this.creatures?.applySnapshot(snapshot.creatures);
    this.events.emit('hud:dirty', undefined);
  }

  /** Ortak oyun odası kur (mevcut dünya paylaşılır). */
  hostRoom(): string | null {
    const world = this.world;
    if (!world || !this.meta) return null;
    const code = Multiplayer.newRoomCode();
    this.mp.connect({
      world,
      code,
      name: this.profile.name,
      host: true,
      meta: { seed: world.params.seed, mode: world.params.mode, difficulty: world.params.difficulty.id, name: this.meta.name },
      makeState: () => this.multiplayerState(),
      makeSnapshot: () => this.multiplayerSnapshot(),
    });
    return code;
  }

  /** Var olan odaya katıl (dünya parametreleri sunucudan gelir). */
  joinRoom(code: string): void {
    const world = this.world;
    if (!world) {
      this.events.emit('toast', { text: 'Önce bir dünyaya gir', kind: 'bad' });
      return;
    }
    this.mp.connect({
      world,
      code,
      name: this.profile.name,
      host: false,
      meta: { seed: world.params.seed, mode: world.params.mode, difficulty: world.params.difficulty.id, name: this.meta?.name ?? 'Ortak' },
    });
  }

  /** Yönetici gözlemcisi: geçici bir istemci dünyası açar; sunucunun welcome
   * snapshot'ı bunu authoritative oda görünümüyle hemen değiştirir. */
  observeRoom(code: string, ticket: string): void {
    if (!/^[A-Z0-9]{16,64}$/.test(code) || ticket.length < 32) {
      this.events.emit('toast', { text: 'Gözlemci bileti geçersiz', kind: 'bad' });
      return;
    }
    this.setState('loading');
    this.teardownWorld();
    this.observerMode = true;
    const now = Date.now();
    const meta: WorldMeta = { id: `observer-${code}`, name: 'Gözlem', mode: 'normal', difficulty: 'normal', seed: 0, createdAt: now, updatedAt: now, playtime: 0, day: 1 };
    const world = new World({ seed: 0, mode: 'normal', difficulty: 'normal', name: meta.name }, this.settings);
    const player = new Player(this.render.camera);
    this.attach(world, player, meta);
    const spawn = this.findSpawn(world);
    player.placeAt(world, spawn.x, spawn.z);
    this.mp.connect({
      world,
      code,
      name: 'Yönetici',
      host: false,
      observerTicket: ticket,
      meta: { seed: 0, mode: 'normal', difficulty: 'normal', name: 'Gözlem' },
    });
    this.setState('playing');
  }

  leaveRoom(): void {
    if (this.observerMode) {
      this.exitToMenu();
      return;
    }
    this.mp.disconnect();
  }

  rotateBuild(): void {
    if (this.observerMode) return;
    this.buildRotation = (this.buildRotation + 1) % 4;
  }

  private frame(dt: number): void {
    const { world, player } = this;
    if (!world || !player) {
      return;
    }
    if (this.state === 'playing' || this.state === 'paused') {
      world.update(player.position.x, player.position.z, this.clock.elapsed);
      const biome = world.biomeAt(player.position.x, player.position.z);
      const underwater = player.position.y + player.eyeHeight < WATER_LEVEL;
      this.render.update(dt, this.clock, world.scene, player.position, biome.biome, underwater);

      // Yerleştirme hayaleti
      const pieceId = this.state === 'playing' ? this.heldPiece() : null;
      if (pieceId && this.ghost) {
        this.ghost.update(planPlacement(world, player, pieceId, this.buildRotation));
        this.aimedResource = null;
      } else {
        this.ghost?.hide();
        // Nişan alınan kaynak bilgisi (HUD)
        const t = this.state === 'playing' ? pickResource(world, player) : null;
        this.aimedResource = t ? { id: t.instance.id, name: t.def.name, progress: harvestProgress(t.instance.id, t.def) } : null;
      }
      // Çok oyunculu: konumu yayınla, uzak oyuncuları yumuşat
      if (this.mp.status !== 'off') {
        this.mp.update(dt, player.position, player.yaw, player.stats.health);
      }

      this.events.emit('hud:dirty', undefined);
    }
    this.render.render(world.scene);
  }

  /** Sandık içeriğine erişim (UI için). */
  chestOf(structureId: string): PlacedStructure | null {
    return this.world?.structureById(structureId) ?? null;
  }
}

/** Canlı kimliğinden okunabilir ad (ölüm mesajları için). */
function creatureLabel(defId: string): string {
  const map: Record<string, string> = {
    wolf: 'kurt',
    boar: 'yaban domuzu',
    ice_wolf: 'buz kurdu',
    swamp_lurker: 'bataklık sinsisi',
    venom_crawler: 'zehir sürüngeni',
    venom_stalker: 'zehir avcısı',
    skeleton_guard: 'iskelet muhafız',
    castle_soldier: 'kızıl kale askeri',
    goat: 'dağ keçisi',
    crab: 'yengeç',
  };
  return map[defId] ?? defId;
}
